import React, { useMemo } from 'react';

import { axisScale, referenceMarks, scaleBounds, xAxisName, xLabelSpacing, xTitleRoom, yAxisName } from '../axisStyle';
import { formatAxis, formatValue, memberLabel, unitName } from '../format';
import type { Palette } from '../palette';
import type { Block, Envelope, EnvelopeColumn } from '../types';

import Chart, { type ChartOption } from './Chart';

const LABEL_LIMIT = 18;

export function bridgeOption(envelope: Envelope, palette: Palette, block?: Block): ChartOption {
  const scale = axisScale(block);
  const measure = envelope.columns.find((column) => column.role === 'measure') as EnvelopeColumn;
  const dim = envelope.columns.find((column) => column.role === 'dim') as EnvelopeColumn;
  const comparison = envelope.period.comparisons[0];
  const start = (envelope.totals?.[comparison.id]?.[measure.name] as number | null) ?? 0;
  const end = (envelope.totals?.current?.[measure.name] as number | null) ?? 0;
  const before = new Map((envelope.compare[comparison.id] ?? []).map((row) => [String(row[dim.name]), (row[measure.name] as number | null) ?? 0]));
  const members = new Set([...envelope.rows.map((row) => String(row[dim.name])), ...before.keys()]);
  const now = new Map(envelope.rows.map((row) => [String(row[dim.name]), (row[measure.name] as number | null) ?? 0]));
  const steps = [...members]
    .map((member) => ({ member, change: (now.get(member) ?? 0) - (before.get(member) ?? 0) }))
    .filter((step) => step.change !== 0)
    .sort((a, b) => b.change - a.change);
  let level = start;
  let low = Math.min(start, end);
  const bases: Array<number> = [];
  for (const step of steps) {
    bases.push(step.change >= 0 ? level : level + step.change);
    level += step.change;
    low = Math.min(low, level);
  }
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(1, Math.abs(low) * 0.7)));
  const floor = scale.zero ? Math.min(0, low) : scale.min ?? (low > 0 ? Math.floor((low * 0.7) / magnitude) * magnitude : low);
  const categories = [comparison.label, ...steps.map((step) => memberLabel(step.member)), envelope.period.current.label];
  const label = (text: string) => (text.length > LABEL_LIMIT ? `${text.slice(0, LABEL_LIMIT - 1)}…` : text);
  return {
    animation: false,
    textStyle: { fontFamily: palette.sans, fontSize: 11, color: palette.ink3 },
    grid: { left: 4, right: 8, top: 22, bottom: 4 + xTitleRoom(block), containLabel: true },
    tooltip: { show: false },
    xAxis: {
      type: 'category',
      data: categories,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: palette.hairlineStrong } },
      ...xAxisName(block, palette),
      axisLabel: { color: palette.ink2, fontSize: 11, interval: 0, hideOverlap: true, formatter: label, ...xLabelSpacing(block) },
    },
    yAxis: { type: 'value', ...scaleBounds(scale), min: floor, triggerEvent: true, splitNumber: 4, ...yAxisName(block, unitName(measure.format, palette.ink3), palette), axisLabel: { color: palette.ink3, fontSize: 11, formatter: (value: number) => formatAxis(value, measure.format) }, splitLine: { lineStyle: { color: palette.hairline } } },
    series: [
      { type: 'bar', stack: 'bridge', silent: true, itemStyle: { color: 'transparent' }, data: [floor, ...bases, floor] },
      {
        type: 'bar',
        stack: 'bridge',
        barMaxWidth: 46,
        labelLayout: { hideOverlap: true },
        ...referenceMarks(block, palette, measure.format),
        data: [
          { value: start - floor, itemStyle: { color: palette.series[0] } },
          ...steps.map((step) => ({ value: Math.abs(step.change), itemStyle: { color: step.change >= 0 ? palette.pos : palette.neg } })),
          { value: end - floor, itemStyle: { color: palette.series[0] } },
        ],
        label: {
          show: true,
          position: 'top',
          fontSize: 11,
          fontFamily: palette.sans,
          color: palette.ink2,
          formatter: ({ dataIndex }: { dataIndex: number }) =>
            dataIndex === 0 ? formatValue(start, measure.format) : dataIndex === steps.length + 1 ? formatValue(end, measure.format) : `${steps[dataIndex - 1].change >= 0 ? '+' : '−'}${formatValue(Math.abs(steps[dataIndex - 1].change), measure.format)}`,
        },
      },
    ],
  };
}

export default function Bridge({ block, envelope, palette }: { block: Block; envelope: Envelope; palette: Palette }) {
  const option = useMemo(() => bridgeOption(envelope, palette, block), [envelope, palette, block]);
  return <Chart className="chart short" option={option} />;
}
